/**
 * Keymap editor model (W2-13 settings-keymap; DESIGN.md §4.20, §5.15, §8.1; ARCHITECTURE.md §6.3).
 *
 * Pure functions behind the keymap editor and the key recorder, written against the frozen contract only
 * (`KeymapService`, `KeyBinding`, RESERVED_CHORDS / RESERVED_CHORDS_MAC / UNRELIABLE_CHORDS):
 *  - chord helpers for canonical chords (`[hold:][Ctrl+][Alt+][Shift+]<code>`, `[Ctrl+][Alt+]char:<c>`, wheel);
 *  - the recorder rule: reserved chords (every OS, plus the macOS list on a Mac) and Ctrl+Alt (AltGr) are refused
 *    with a reason, unreliable chords are accepted with a warning;
 *  - editor rows: one per command and argument set, with the effective bindings (preset or yours) and your removals
 *    (`remove: true` overrides), split into removals that apply to the current preset and removals that only apply
 *    to the other preset (the overrides are shared by both) or to no preset key any more;
 *  - override edits (add, change, remove, restore, reset), always returning a new override list for
 *    `KeymapService.setOverrides`, which persists it in settings.keyboard.overrides;
 *  - a display parser for `when` conditions (context column) and the search haystack of a row.
 * Settings are the source of truth: overrides are read from settings.keyboard.overrides and written through the
 * keymap service. The effective list (`keymap.bindings()`) already has the Alt-free and macOS transforms applied.
 */

import {
  RESERVED_CHORDS,
  RESERVED_CHORDS_MAC,
  UNRELIABLE_CHORDS,
  type Chord,
  type CommandDef,
  type FeatureModule,
  type KeyBinding,
  type KeymapPresetId,
  type KeymapService,
} from '../../app/registry/types';
import type { Json, MessageRef } from '../../core/util/types';

// ---------------------------------------------------------------------------------------------------------------
// Platform
// ---------------------------------------------------------------------------------------------------------------

export type Os = 'mac' | 'other';

interface NavigatorLike {
  readonly platform?: string;
  readonly userAgent?: string;
  readonly userAgentData?: { readonly platform?: string };
}

/** macOS (and iPadOS with a keyboard) use Cmd where chords say Ctrl. */
export function detectOs(
  nav: NavigatorLike | undefined = typeof navigator === 'undefined' ? undefined : navigator,
): Os {
  if (nav === undefined) return 'other';
  const platform = nav.userAgentData?.platform ?? nav.platform ?? '';
  if (/mac|iphone|ipad|ipod/i.test(platform)) return 'mac';
  return /Macintosh|Mac OS X|iPhone|iPad/.test(nav.userAgent ?? '') ? 'mac' : 'other';
}

/** Optional members of the app keymap beyond the contract (feature-detected; see change request W2-13 CR-1). */
/** The OS the keymap applies its macOS transform for (KeymapService.os; otherwise detected). */
export function keymapOs(keymap: KeymapService): Os {
  return keymap.os ?? detectOs();
}

/** A problem the app keymap found in your own shortcuts (reserved chord in an imported file, unknown command...). */
export interface OverrideIssue {
  readonly level: 'error' | 'warning';
  readonly code: string;
  readonly text: MessageRef;
  readonly binding: KeyBinding | null;
}

/**
 * Issues about the user's overrides, when the keymap reports them (KeymapService.issues, CR-W2-13-1; module issues
 * are the preset audit's business and are left out).
 */
export function overrideIssues(keymap: KeymapService): readonly OverrideIssue[] {
  let raw: ReturnType<NonNullable<KeymapService['issues']>>;
  try {
    raw = keymap.issues?.() ?? [];
  } catch {
    return [];
  }
  const out: OverrideIssue[] = [];
  for (const item of raw) {
    if (item.moduleId !== null) continue;
    out.push({ level: item.level, code: item.code, text: item.text, binding: item.binding ?? null });
  }
  return out;
}

// ---------------------------------------------------------------------------------------------------------------
// Chords
// ---------------------------------------------------------------------------------------------------------------

export interface ChordParts {
  readonly kind: 'key' | 'char' | 'wheel';
  readonly hold: boolean;
  readonly ctrl: boolean;
  readonly alt: boolean;
  readonly shift: boolean;
  /** KeyboardEvent.code ('key'), the character ('char') or '' ('wheel'). */
  readonly key: string;
}

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
const DIGITS = '0123456789'.split('');

/** `KeyboardEvent.code` names a chord may use, by lower-cased name (the chord grammar is case-insensitive). */
const CODE_BY_LOWER: ReadonlyMap<string, string> = new Map(
  [
    ...LETTERS.map((l) => `Key${l}`),
    ...DIGITS.map((d) => `Digit${d}`),
    ...DIGITS.map((d) => `Numpad${d}`),
    ...Array.from({ length: 24 }, (_, i) => `F${String(i + 1)}`),
    'Minus',
    'Equal',
    'BracketLeft',
    'BracketRight',
    'Backslash',
    'Semicolon',
    'Quote',
    'Backquote',
    'Comma',
    'Period',
    'Slash',
    'IntlBackslash',
    'IntlRo',
    'IntlYen',
    'Space',
    'Enter',
    'Escape',
    'Tab',
    'Backspace',
    'Delete',
    'Insert',
    'Home',
    'End',
    'PageUp',
    'PageDown',
    'ArrowUp',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'ContextMenu',
    'Pause',
    'PrintScreen',
    'NumpadAdd',
    'NumpadSubtract',
    'NumpadMultiply',
    'NumpadDivide',
    'NumpadDecimal',
    'NumpadEqual',
    'NumpadComma',
  ].map((code) => [code.toLowerCase(), code] as const),
);

/** Short names people write for keys ("K", "1", "Esc", "Up", ",") and the code they stand for. */
const KEY_ALIASES: ReadonlyMap<string, string> = new Map<string, string>([
  ...LETTERS.map((l): [string, string] => [l.toLowerCase(), `Key${l}`]),
  ...DIGITS.map((d): [string, string] => [d, `Digit${d}`]),
  ['esc', 'Escape'],
  ['del', 'Delete'],
  ['ins', 'Insert'],
  ['return', 'Enter'],
  ['numpadenter', 'Enter'],
  ['spacebar', 'Space'],
  ['pgup', 'PageUp'],
  ['pgdn', 'PageDown'],
  ['up', 'ArrowUp'],
  ['down', 'ArrowDown'],
  ['left', 'ArrowLeft'],
  ['right', 'ArrowRight'],
  [',', 'Comma'],
  ['.', 'Period'],
  ['/', 'Slash'],
  [';', 'Semicolon'],
  ["'", 'Quote'],
  ['`', 'Backquote'],
  ['[', 'BracketLeft'],
  [']', 'BracketRight'],
  ['\\', 'Backslash'],
  ['-', 'Minus'],
  ['=', 'Equal'],
]);

/**
 * The canonical `KeyboardEvent.code` for a key name written in a chord: code names in any case ("keyk" ->
 * "KeyK") and the usual short names ("K" -> "KeyK", "1" -> "Digit1", "Esc" -> "Escape", "," -> "Comma"). Names it
 * does not know (codes of rarer keys) are kept as written.
 */
export function canonicalKey(name: string): string {
  const lower = name.trim().toLowerCase();
  return CODE_BY_LOWER.get(lower) ?? KEY_ALIASES.get(lower) ?? name.trim();
}

const CTRL_TOKENS: ReadonlySet<string> = new Set(['ctrl', 'control', 'cmd', 'command', 'meta', 'mod', 'primary']);
const ALT_TOKENS: ReadonlySet<string> = new Set(['alt', 'option', 'opt']);

/**
 * Splits a chord written in the contract grammar (modifiers in any order and case, key names as codes or their
 * short names); null when it has no key.
 */
export function chordParts(chord: string): ChordParts | null {
  let rest = chord.trim();
  let hold = false;
  if (rest.toLowerCase().startsWith('hold:')) {
    hold = true;
    rest = rest.slice(5);
  }
  const charAt = rest.indexOf('char:');
  const head = charAt >= 0 ? rest.slice(0, charAt) : rest.slice(0, Math.max(0, rest.lastIndexOf('+') + 1));
  const key = charAt >= 0 ? rest.slice(charAt + 5) : rest.slice(head.length).trim();
  if (key === '') return null;
  const mods = head
    .split('+')
    .map((m) => m.trim().toLowerCase())
    .filter((m) => m !== '');
  const ctrl = mods.some((m) => CTRL_TOKENS.has(m));
  const alt = mods.some((m) => ALT_TOKENS.has(m));
  const shift = mods.includes('shift');
  if (charAt >= 0) return { kind: 'char', hold: false, ctrl, alt, shift: false, key };
  if (key.toLowerCase() === 'wheel') return { kind: 'wheel', hold: false, ctrl, alt, shift, key: '' };
  return { kind: 'key', hold, ctrl, alt, shift, key: canonicalKey(key) };
}

/** Canonical text of chord parts (modifier order Ctrl, Alt, Shift). */
export function formatParts(p: ChordParts): Chord {
  const mods = `${p.ctrl ? 'Ctrl+' : ''}${p.alt ? 'Alt+' : ''}`;
  if (p.kind === 'char') return `${mods}char:${p.key}`;
  const body = `${mods}${p.shift ? 'Shift+' : ''}${p.kind === 'wheel' ? 'Wheel' : p.key}`;
  return p.hold && p.kind === 'key' ? `hold:${body}` : body;
}

/** Canonical form for comparisons (unparsable chords compare as written). */
export function normChord(chord: string): Chord {
  const p = chordParts(chord);
  return p === null ? chord.trim() : formatParts(p);
}

/** The key combination that triggers a chord (without `hold:`). */
export function triggerOf(chord: string): Chord {
  const p = chordParts(chord);
  return p === null ? chord.trim() : formatParts({ ...p, hold: false });
}

export function isHoldChord(chord: string): boolean {
  return chordParts(chord)?.hold ?? false;
}

export function chordHasAlt(chord: string): boolean {
  return chordParts(chord)?.alt ?? false;
}

/**
 * The chord to store for a recorded key press, with the recorder's options: `byChar` binds the produced character
 * instead of the key position (D§8.1 "by character"; only for a single printable character), `hold` makes a
 * press-and-hold binding (keys only).
 */
export function recordedChord(
  base: Chord,
  opts: { readonly byChar: boolean; readonly hold: boolean; readonly char: string | null },
): Chord {
  const p = chordParts(base);
  if (p === null) return base;
  if (opts.byChar && opts.char !== null && p.kind === 'key') {
    return formatParts({ kind: 'char', hold: false, ctrl: p.ctrl, alt: p.alt, shift: false, key: opts.char });
  }
  return formatParts({ ...p, hold: opts.hold && p.kind === 'key' });
}

/** A single printable character produced by a key press (for "by character" bindings), else null. */
export function printableChar(key: string): string | null {
  if (key.length === 0 || key === ' ') return null;
  let count = 0;
  for (const _cp of key) count++;
  return count === 1 ? key : null;
}

/**
 * The chords a key press can trigger, for "find a command by pressing its keys": the key position (`code` chord,
 * as the keymap's `chordOf` reports it) and, for a printable character, the matching `char:` chord with the same
 * Ctrl / Alt.
 */
export function pressedChords(codeChord: Chord, key: string): Chord[] {
  const base = normChord(codeChord);
  const p = chordParts(codeChord);
  const ch = printableChar(key);
  if (p === null || p.kind !== 'key' || ch === null) return [base];
  return [base, formatParts({ kind: 'char', hold: false, ctrl: p.ctrl, alt: p.alt, shift: false, key: ch })];
}

// ---------------------------------------------------------------------------------------------------------------
// Recorder rule (DESIGN.md §8.1)
// ---------------------------------------------------------------------------------------------------------------

/** Why a chord is refused or warned about (explained by the messages `reason.<id>`). */
export type ChordReason =
  | 'tabsWindows'
  | 'tabSwitch'
  | 'history'
  | 'closeWindow'
  | 'fullscreen'
  | 'devtools'
  | 'privateWindow'
  | 'addons'
  | 'browserTool'
  | 'screenshot'
  | 'reload'
  | 'clearData'
  | 'macApp'
  | 'macWindow'
  | 'macPrefs'
  | 'macCycle'
  | 'macHistory'
  | 'macAddress'
  | 'macLogout'
  | 'macOther'
  | 'ctrlAlt'
  | 'unreliableMenu'
  | 'unreliableAddress'
  | 'unreliableDownloads'
  | 'unreliableHistory'
  | 'unreliableOther';

/** `ref`: the keymap's own translated reason (KeymapService.check), shown instead of the local reason text. */
export type ChordVerdict =
  | { readonly status: 'ok'; readonly chord: Chord }
  | {
      readonly status: 'unreliable';
      readonly chord: Chord;
      readonly reason: ChordReason;
      readonly trigger: Chord;
      readonly ref?: MessageRef;
    }
  | {
      readonly status: 'reserved' | 'ctrlAlt';
      readonly chord: Chord;
      readonly reason: ChordReason;
      readonly trigger: Chord;
      readonly ref?: MessageRef;
    };

const RESERVED_REASONS: ReadonlyMap<Chord, ChordReason> = (() => {
  const map = new Map<Chord, ChordReason>();
  const set = (chords: readonly Chord[], reason: ChordReason): void => {
    for (const c of chords) map.set(c, reason);
  };
  set(
    ['Ctrl+KeyT', 'Ctrl+KeyN', 'Ctrl+KeyW', 'Ctrl+KeyQ', 'Ctrl+Shift+KeyT', 'Ctrl+Shift+KeyN', 'Ctrl+Shift+KeyW'],
    'tabsWindows',
  );
  set(['Ctrl+Shift+KeyQ'], 'tabsWindows');
  set(['Ctrl+Tab', 'Ctrl+Shift+Tab', 'Ctrl+PageUp', 'Ctrl+PageDown'], 'tabSwitch');
  for (let d = 1; d <= 9; d++) {
    map.set(`Ctrl+Digit${String(d)}`, 'tabSwitch');
    map.set(`Alt+Digit${String(d)}`, 'tabSwitch');
  }
  set(['Alt+ArrowLeft', 'Alt+ArrowRight', 'Alt+Home'], 'history');
  set(['Alt+F4'], 'closeWindow');
  set(['F11'], 'fullscreen');
  set(
    ['F12', 'Ctrl+Shift+KeyI', 'Ctrl+Shift+KeyJ', 'Ctrl+Shift+KeyC', 'Ctrl+Shift+KeyK', 'Ctrl+Shift+KeyE'],
    'devtools',
  );
  set(['Ctrl+Shift+KeyP'], 'privateWindow');
  set(['Ctrl+Shift+KeyA'], 'addons');
  set(['Ctrl+Shift+KeyM'], 'browserTool');
  set(['Ctrl+Shift+KeyS'], 'screenshot');
  set(['Ctrl+KeyR', 'Ctrl+Shift+KeyR'], 'reload');
  set(['Ctrl+Shift+Delete'], 'clearData');
  return map;
})();

const MAC_REASONS: ReadonlyMap<Chord, ChordReason> = new Map<Chord, ChordReason>([
  ['Ctrl+KeyQ', 'macApp'],
  ['Ctrl+KeyW', 'macApp'],
  ['Ctrl+KeyT', 'macApp'],
  ['Ctrl+KeyN', 'macApp'],
  ['Ctrl+KeyH', 'macWindow'],
  ['Ctrl+KeyM', 'macWindow'],
  ['Ctrl+Comma', 'macPrefs'],
  ['Ctrl+Backquote', 'macCycle'],
  ['Ctrl+BracketLeft', 'macHistory'],
  ['Ctrl+BracketRight', 'macHistory'],
  ['Ctrl+ArrowLeft', 'macHistory'],
  ['Ctrl+ArrowRight', 'macHistory'],
  ['Ctrl+KeyL', 'macAddress'],
  ['Ctrl+Shift+KeyQ', 'macLogout'],
]);

const UNRELIABLE_REASONS: ReadonlyMap<Chord, ChordReason> = new Map<Chord, ChordReason>([
  ['Alt+KeyD', 'unreliableMenu'],
  ['Alt+KeyE', 'unreliableMenu'],
  ['Alt+KeyF', 'unreliableMenu'],
  ['F6', 'unreliableAddress'],
  ['Ctrl+KeyL', 'unreliableAddress'],
  ['Ctrl+KeyJ', 'unreliableDownloads'],
  ['Ctrl+KeyH', 'unreliableHistory'],
]);

const RESERVED = new Set(RESERVED_CHORDS.map(normChord));
const RESERVED_MAC = new Set(RESERVED_CHORDS_MAC.map(normChord));
const UNRELIABLE = new Set(UNRELIABLE_CHORDS.map(normChord));

/**
 * The recorder rule: refused = in RESERVED_CHORDS (every OS), in RESERVED_CHORDS_MAC (on macOS, where Ctrl is Cmd)
 * or Ctrl+Alt (AltGr on European layouts); warned = UNRELIABLE_CHORDS; everything else is fine. Wheel and `char:`
 * chords are never reserved (Ctrl+Alt still applies to them).
 */
/**
 * The recorder rule for a chord: the keymap's own rule and reason when it has one (KeymapService.check, one reason
 * table for the whole app, CR-W2-13-1), else the local copy (`checkChord`).
 */
export function verdictOf(keymap: KeymapService, chord: Chord, os: Os): ChordVerdict {
  const local = checkChord(chord, os);
  if (keymap.check === undefined) return local;
  let checked: ReturnType<NonNullable<KeymapService['check']>>;
  try {
    checked = keymap.check(chord);
  } catch {
    return local;
  }
  if (!('reason' in checked)) return { status: 'ok', chord: local.chord };
  const { status, reason: ref } = checked;
  const fallback: ChordReason =
    status === 'ctrlAlt' ? 'ctrlAlt' : status === 'unreliable' ? 'unreliableOther' : 'browserTool';
  const reason = local.status === 'ok' ? fallback : local.reason;
  const trigger = local.status === 'ok' ? local.chord : local.trigger;
  return status === 'unreliable'
    ? { status, chord: local.chord, reason, trigger, ref }
    : { status, chord: local.chord, reason, trigger, ref };
}

export function checkChord(chord: Chord, os: Os): ChordVerdict {
  const p = chordParts(chord);
  const canonical = normChord(chord);
  if (p === null) return { status: 'ok', chord: canonical };
  const trigger = formatParts({ ...p, hold: false });
  if (p.kind === 'key') {
    if (RESERVED.has(trigger)) {
      return { status: 'reserved', chord: canonical, trigger, reason: RESERVED_REASONS.get(trigger) ?? 'browserTool' };
    }
    if (os === 'mac' && RESERVED_MAC.has(trigger)) {
      return { status: 'reserved', chord: canonical, trigger, reason: MAC_REASONS.get(trigger) ?? 'macOther' };
    }
  }
  if (p.ctrl && p.alt) return { status: 'ctrlAlt', chord: canonical, trigger, reason: 'ctrlAlt' };
  if (p.kind === 'key' && UNRELIABLE.has(trigger)) {
    return {
      status: 'unreliable',
      chord: canonical,
      trigger,
      reason: UNRELIABLE_REASONS.get(trigger) ?? 'unreliableOther',
    };
  }
  return { status: 'ok', chord: canonical };
}

export function isRefused(v: ChordVerdict): boolean {
  return v.status === 'reserved' || v.status === 'ctrlAlt';
}

// ---------------------------------------------------------------------------------------------------------------
// Bindings and overrides
// ---------------------------------------------------------------------------------------------------------------

/** Condition text normalised for identity comparisons. */
export function whenKey(when: string | undefined): string {
  return when === undefined ? '' : when.replace(/\s+/g, ' ').trim();
}

function stableJson(v: Json): string {
  if (Array.isArray(v)) return `[${v.map((x: Json) => stableJson(x)).join(',')}]`;
  if (v !== null && typeof v === 'object') {
    const o = v as { readonly [k: string]: Json };
    return `{${Object.keys(o)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${stableJson(o[k] as Json)}`)
      .join(',')}}`;
  }
  return JSON.stringify(v);
}

/** Stable text of command arguments ('' for none). */
export function argsKey(args: Json | undefined): string {
  return args === undefined ? '' : stableJson(args);
}

/** Row identity: command and arguments. */
export function rowKeyOf(command: string, args: Json | undefined): string {
  return `${command}\u0000${argsKey(args)}`;
}

/** Same chord, command, condition and arguments. */
export function sameBinding(a: KeyBinding, b: KeyBinding): boolean {
  return (
    a.command === b.command &&
    normChord(a.chord) === normChord(b.chord) &&
    whenKey(a.when) === whenKey(b.when) &&
    argsKey(a.args) === argsKey(b.args)
  );
}

/** A clean user binding (no preset-only fields) with exact optional properties. */
export function userBinding(b: {
  readonly chord: Chord;
  readonly command: string;
  readonly args?: Json | undefined;
  readonly when?: string | undefined;
}): KeyBinding {
  return {
    chord: normChord(b.chord),
    command: b.command,
    ...(b.args === undefined ? {} : { args: b.args }),
    ...(b.when === undefined || whenKey(b.when) === '' ? {} : { when: b.when }),
  };
}

/** The `remove: true` override that deletes a preset binding (matched by command, condition and chord). */
export function removalOf(b: KeyBinding): KeyBinding {
  return { ...userBinding(b), remove: true };
}

export interface RowBinding {
  /** The binding as it applies now (transformed chord). */
  readonly binding: KeyBinding;
  readonly source: 'preset' | 'user';
}

/** A removal of yours that does not apply to the current preset. */
export interface InactiveRemoval {
  readonly binding: KeyBinding;
  /** The other preset whose key it removes, or null: it matches no preset key any more (no effect). */
  readonly preset: KeymapPresetId | null;
}

export interface KeymapRow {
  readonly key: string;
  readonly commandId: string;
  /** Undefined for a binding whose command is not registered (an override from another version). */
  readonly command: CommandDef | undefined;
  readonly args: Json | undefined;
  readonly bindings: readonly RowBinding[];
  /** Your removals of keys of the current preset (`remove: true` overrides) for this command and arguments. */
  readonly removed: readonly KeyBinding[];
  /** Your removals that leave the current preset's keys alone (they belong to the other preset, or to none). */
  readonly removedElsewhere: readonly InactiveRemoval[];
  /** You changed this row (additions or removals, in either preset: settings.keyboard.overrides is shared). */
  readonly changed: boolean;
}

/** The preset keys declared by the modules, for telling which preset a removal belongs to. */
export interface PresetLists {
  readonly current: KeymapPresetId;
  readonly lists: Readonly<Record<KeymapPresetId, readonly KeyBinding[]>>;
}

const PRESET_IDS: readonly KeymapPresetId[] = ['default', 'rpe'];

/** A module's keys in a preset (a module without an `rpe` list shares its `default` list). */
export function moduleBindings(mod: FeatureModule, preset: KeymapPresetId): readonly KeyBinding[] {
  const kb = mod.keybindings;
  if (kb === undefined) return [];
  return kb[preset] ?? (preset === 'default' ? [] : (kb.default ?? []));
}

/**
 * The declared keys of both presets, or null when no module declares any (then every removal is taken to apply).
 */
export function presetListsOf(modules: readonly FeatureModule[], current: KeymapPresetId): PresetLists | null {
  const list = (preset: KeymapPresetId): KeyBinding[] =>
    modules.flatMap((mod) => moduleBindings(mod, preset)).filter((b) => b.remove !== true);
  const lists = { default: list('default'), rpe: list('rpe') };
  return lists.default.length === 0 && lists.rpe.length === 0 ? null : { current, lists };
}

/**
 * Does a `remove: true` override delete this declared preset key? Same command and condition, and its chord is the
 * declared chord or the declared Alt-free / macOS replacement (the editor stores the chord as it applies now).
 */
export function removalHits(removal: KeyBinding, declared: KeyBinding): boolean {
  if (removal.command !== declared.command || whenKey(removal.when) !== whenKey(declared.when)) return false;
  const chord = normChord(removal.chord);
  return [declared.chord, declared.altFree, declared.mac].some((c) => typeof c === 'string' && normChord(c) === chord);
}

/** Which preset a removal applies to: the current one, the other one, or none (null). */
export function removalPreset(removal: KeyBinding, presets: PresetLists): KeymapPresetId | null {
  if (presets.lists[presets.current].some((b) => removalHits(removal, b))) return presets.current;
  return PRESET_IDS.find((p) => p !== presets.current && presets.lists[p].some((b) => removalHits(removal, b))) ?? null;
}

/** Is this effective binding one of your additions? */
export function isUserBinding(b: KeyBinding, overrides: readonly KeyBinding[]): boolean {
  return overrides.some((o) => o.remove !== true && sameBinding(o, b));
}

/**
 * Editor rows: one per registered command (arguments undefined) plus one per other argument set that a binding
 * or an override uses (e.g. "Beat snap 1/4" and "Beat snap 1/8" of one `snap.set` command), plus rows for bindings
 * of unknown commands so they can be removed. Order: command order of `commands`, argument rows after their
 * command, unknown commands last.
 */
export function buildRows(
  commands: readonly CommandDef[],
  effective: readonly KeyBinding[],
  overrides: readonly KeyBinding[],
  presets: PresetLists | null = null,
): KeymapRow[] {
  interface Acc {
    commandId: string;
    command: CommandDef | undefined;
    args: Json | undefined;
    bindings: RowBinding[];
    removed: KeyBinding[];
    removedElsewhere: InactiveRemoval[];
    changed: boolean;
  }
  const byKey = new Map<string, Acc>();
  const commandById = new Map(commands.map((c) => [c.id, c] as const));
  const ensure = (commandId: string, args: Json | undefined): Acc => {
    const key = rowKeyOf(commandId, args);
    let acc = byKey.get(key);
    if (acc === undefined) {
      acc = {
        commandId,
        command: commandById.get(commandId),
        args,
        bindings: [],
        removed: [],
        removedElsewhere: [],
        changed: false,
      };
      byKey.set(key, acc);
    }
    return acc;
  };
  for (const c of commands) ensure(c.id, undefined);
  for (const b of effective) {
    const acc = ensure(b.command, b.args);
    if (acc.bindings.some((x) => sameBinding(x.binding, b))) continue;
    acc.bindings.push({ binding: b, source: isUserBinding(b, overrides) ? 'user' : 'preset' });
  }
  for (const o of overrides) {
    const acc = ensure(o.command, o.args);
    acc.changed = true;
    if (o.remove !== true) continue;
    if (acc.removed.some((r) => sameBinding(r, o)) || acc.removedElsewhere.some((r) => sameBinding(r.binding, o))) {
      continue;
    }
    const preset = presets === null ? null : removalPreset(o, presets);
    if (presets === null || preset === presets.current) acc.removed.push(o);
    else acc.removedElsewhere.push({ binding: o, preset });
  }
  const order = new Map(commands.map((c, i) => [c.id, i] as const));
  const rank = (a: Acc): number => order.get(a.commandId) ?? Number.MAX_SAFE_INTEGER;
  return [...byKey.entries()]
    .map(([key, a]) => ({ key, ...a }))
    .sort((a, b) => {
      const r = rank(a) - rank(b);
      if (r !== 0) return r;
      if (a.commandId !== b.commandId) return a.commandId < b.commandId ? -1 : 1;
      if (a.args === undefined) return b.args === undefined ? 0 : -1;
      if (b.args === undefined) return 1;
      const ka = argsKey(a.args);
      const kb = argsKey(b.args);
      return ka < kb ? -1 : ka > kb ? 1 : 0;
    })
    .map((a) => ({
      key: a.key,
      commandId: a.commandId,
      command: a.command,
      args: a.args,
      bindings: a.bindings,
      removed: a.removed,
      removedElsewhere: a.removedElsewhere,
      changed: a.changed,
    }));
}

/** Add a binding of yours (no duplicate). */
export function addBinding(overrides: readonly KeyBinding[], b: KeyBinding): KeyBinding[] {
  const clean = userBinding(b);
  // Re-adding a preset binding you removed: restoring it is the same thing.
  const removal = overrides.find((o) => o.remove === true && sameBinding(o, clean));
  if (removal !== undefined) return overrides.filter((o) => o !== removal);
  if (overrides.some((o) => o.remove !== true && sameBinding(o, clean))) return [...overrides];
  return [...overrides, clean];
}

/** Remove (unbind) an effective binding: drops your addition, or records a removal of the preset binding. */
export function removeBinding(overrides: readonly KeyBinding[], rb: RowBinding): KeyBinding[] {
  if (rb.source === 'user') return overrides.filter((o) => !(o.remove !== true && sameBinding(o, rb.binding)));
  const removal = removalOf(rb.binding);
  if (overrides.some((o) => o.remove === true && sameBinding(o, removal))) return [...overrides];
  return [...overrides, removal];
}

/** Change the chord of an effective binding (keeps its condition and arguments). */
export function replaceBinding(overrides: readonly KeyBinding[], rb: RowBinding, chord: Chord): KeyBinding[] {
  if (normChord(chord) === normChord(rb.binding.chord)) return [...overrides];
  const next = { ...userBinding(rb.binding), chord: normChord(chord) };
  if (rb.source === 'user') {
    const replaced = overrides.map((o) => (o.remove !== true && sameBinding(o, rb.binding) ? next : o));
    // Two of your bindings may end up identical: keep one.
    return replaced.filter((o, i) => replaced.findIndex((x) => x.remove === o.remove && sameBinding(x, o)) === i);
  }
  return addBinding(removeBinding(overrides, rb), next);
}

/** Bring back a preset binding you removed. */
export function restoreBinding(overrides: readonly KeyBinding[], removal: KeyBinding): KeyBinding[] {
  return overrides.filter((o) => !(o.remove === true && sameBinding(o, removal)));
}

/**
 * Merge imported keys (RPE Hotkey.txt): they replace the keys of the commands they name, yours and the preset's
 * (preset keys get a removal); other commands keep theirs. An imported key equal to a preset key restores it.
 */
export function mergeImportedBindings(
  overrides: readonly KeyBinding[],
  effective: readonly KeyBinding[],
  imported: readonly KeyBinding[],
): KeyBinding[] {
  let next = [...overrides];
  for (const command of new Set(imported.map((b) => b.command))) {
    for (const b of effective.filter((x) => x.command === command)) {
      next = removeBinding(next, { binding: b, source: isUserBinding(b, next) ? 'user' : 'preset' });
    }
  }
  for (const b of imported) next = addBinding(next, userBinding(b));
  return next;
}

/** Drop every override of a row (its additions and removals): back to the preset's keys. */
export function resetRow(overrides: readonly KeyBinding[], row: Pick<KeymapRow, 'commandId' | 'args'>): KeyBinding[] {
  const key = argsKey(row.args);
  return overrides.filter((o) => !(o.command === row.commandId && argsKey(o.args) === key));
}

/**
 * Condition and arguments for a new or changed binding of a command: those of the binding being replaced
 * (`current` chord, written in any form the chord grammar accepts: "Ctrl+K" finds "Ctrl+KeyK", and a key without
 * `hold:` finds its hold binding), else of the command's first binding with the same arguments, else none (the
 * command's own `when` still applies).
 */
export function bindingTemplate(
  effective: readonly KeyBinding[],
  command: string,
  current: Chord | undefined,
  args?: Json,
): { readonly when: string | undefined; readonly args: Json | undefined; readonly replacing: KeyBinding | null } {
  const mine = effective.filter((b) => b.command === command);
  const findCurrent = (chord: Chord): KeyBinding | null => {
    const canonical = normChord(chord);
    const exact = mine.find((b) => normChord(b.chord) === canonical);
    if (exact !== undefined) return exact;
    const trigger = triggerOf(chord);
    return mine.find((b) => triggerOf(b.chord) === trigger) ?? null;
  };
  const replacing = current === undefined || current.trim() === '' ? null : findCurrent(current);
  if (replacing !== null) return { when: replacing.when, args: replacing.args, replacing };
  const key = argsKey(args);
  const sameArgs = mine.find((b) => argsKey(b.args) === key);
  return { when: sameArgs?.when, args: args ?? sameArgs?.args, replacing: null };
}

// ---------------------------------------------------------------------------------------------------------------
// Context (`when`) display
// ---------------------------------------------------------------------------------------------------------------

/** One condition in a context: an atom, or a comparison such as `workspace == chart`. */
export type ContextPart =
  | { readonly kind: 'atom'; readonly atom: string }
  | { readonly kind: 'cmp'; readonly name: string; readonly value: string; readonly negated: boolean };

type WhenNode =
  | { readonly t: 'atom'; readonly name: string }
  | { readonly t: 'cmp'; readonly name: string; readonly value: string; readonly negated: boolean }
  | { readonly t: 'not'; readonly e: WhenNode }
  | { readonly t: 'and' | 'or'; readonly items: readonly WhenNode[] };

function parseWhenForDisplay(src: string): WhenNode | null {
  const toks = src.match(/&&|\|\||==|!=|[()!]|'[^']*'|"[^"]*"|[A-Za-z0-9_][A-Za-z0-9_.-]*/g) ?? [];
  let pos = 0;
  const peek = (): string | undefined => toks[pos];
  const parseOr = (): WhenNode | null => {
    const items: WhenNode[] = [];
    for (;;) {
      const n = parseAnd();
      if (n === null) return null;
      items.push(n);
      if (peek() !== '||') break;
      pos++;
    }
    return items.length === 1 ? (items[0] ?? null) : { t: 'or', items };
  };
  const parseAnd = (): WhenNode | null => {
    const items: WhenNode[] = [];
    for (;;) {
      const n = parseUnary();
      if (n === null) return null;
      items.push(n);
      if (peek() !== '&&') break;
      pos++;
    }
    return items.length === 1 ? (items[0] ?? null) : { t: 'and', items };
  };
  const parseUnary = (): WhenNode | null => {
    const tok = toks[pos++];
    if (tok === undefined) return null;
    if (tok === '!') {
      const e = parseUnary();
      return e === null ? null : { t: 'not', e };
    }
    if (tok === '(') {
      const inner = parseOr();
      if (toks[pos++] !== ')') return null;
      return inner;
    }
    if (!/^[A-Za-z0-9_]/.test(tok)) return null;
    const op = peek();
    if (op === '==' || op === '!=') {
      pos++;
      const raw = toks[pos++];
      if (raw === undefined) return null;
      const value = raw.replace(/^['"]|['"]$/g, '');
      return { t: 'cmp', name: tok, value, negated: op === '!=' };
    }
    return { t: 'atom', name: tok };
  };
  const root = parseOr();
  return root !== null && pos === toks.length ? root : null;
}

/** Alternatives (OR) of condition lists (AND), negated atoms left out; null when there are too many to show. */
function toAlternatives(n: WhenNode): ContextPart[][] | null {
  switch (n.t) {
    case 'atom':
      return [[{ kind: 'atom', atom: n.name }]];
    case 'cmp':
      return [[{ kind: 'cmp', name: n.name, value: n.value, negated: n.negated }]];
    case 'not':
      if (n.e.t === 'cmp') return [[{ kind: 'cmp', name: n.e.name, value: n.e.value, negated: !n.e.negated }]];
      // "not typing in a field", "not during playback": the positive conditions say where the key works.
      return [[]];
    case 'or': {
      const out: ContextPart[][] = [];
      for (const item of n.items) {
        const alt = toAlternatives(item);
        if (alt === null) return null;
        out.push(...alt);
      }
      return out.length > 6 ? null : out;
    }
    case 'and': {
      let acc: ContextPart[][] = [[]];
      for (const item of n.items) {
        const alt = toAlternatives(item);
        if (alt === null) return null;
        const next: ContextPart[][] = [];
        for (const a of acc) for (const b of alt) next.push([...a, ...b]);
        if (next.length > 6) return null;
        acc = next;
      }
      return acc;
    }
  }
}

function partKey(p: ContextPart): string {
  return p.kind === 'atom' ? p.atom : `${p.name}${p.negated ? '!=' : '=='}${p.value}`;
}

/**
 * The situations a binding applies in, for display: a list of alternatives, each a list of conditions that must
 * all hold. `[[]]` = everywhere. `null` = the condition is too involved (or not parsable) to summarise; show it
 * as written.
 */
export function describeWhen(...conditions: ReadonlyArray<string | undefined>): ContextPart[][] | null {
  const parts = conditions.filter((c): c is string => c !== undefined && c.trim() !== '');
  if (parts.length === 0) return [[]];
  const node = parseWhenForDisplay(parts.map((c) => `(${c})`).join(' && '));
  if (node === null) return null;
  const alts = toAlternatives(node);
  if (alts === null) return null;
  // Dedupe conditions within an alternative and identical alternatives.
  const seen = new Set<string>();
  const out: ContextPart[][] = [];
  for (const alt of alts) {
    const uniq: ContextPart[] = [];
    const keys = new Set<string>();
    for (const p of alt) {
      const k = partKey(p);
      if (!keys.has(k)) {
        keys.add(k);
        uniq.push(p);
      }
    }
    const sig = [...keys].sort().join('&');
    if (!seen.has(sig)) {
      seen.add(sig);
      out.push(uniq);
    }
  }
  // An empty alternative ("everywhere") absorbs the others.
  return out.some((a) => a.length === 0) ? [[]] : out;
}

// ---------------------------------------------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------------------------------------------

/** Lower-cased search terms (whitespace separated). */
export function searchTerms(query: string): string[] {
  return query
    .toLowerCase()
    .split(/\s+/)
    .filter((t) => t !== '');
}

/** Every term occurs in one of the texts (case-insensitive). An empty query matches everything. */
export function matchesTerms(terms: readonly string[], texts: ReadonlyArray<string | undefined | null>): boolean {
  if (terms.length === 0) return true;
  const hay = texts
    .filter((t): t is string => typeof t === 'string')
    .join('\n')
    .toLowerCase();
  return terms.every((t) => hay.includes(t));
}

/**
 * Filter modes of the keymap editor: 'conflicts' = real clashes in one situation (red), 'shared' = keys that do
 * different things in different situations (information, UX-061).
 */
export type KeymapFilter = 'all' | 'changed' | 'conflicts' | 'shared' | 'unbound';
